import { isEmpty } from "lodash";
import { useForm } from "react-hook-form";
import { Grid, Table } from "semantic-ui-react";
import { CommonTable, CustomButton } from "..";

import { useMemo, useState } from "react";
import { MdClear, MdDeleteOutline, MdModeEditOutline } from "react-icons/md";
import PlusIcon from "../../assets/commonIcon/PlusIcon";
import { UNIT_TYPES } from "../../config/optionsConstants";
import { ValueAndCostTable } from "../../config/tableColConstant";
import { warningMessage } from "../../helpers/ErrorHandler";
import ConfirmModal from "../confirmViewModal/ConfirmModal";
import { InputNumber } from "../InputText/InputNumber";
import { DropDown } from "../selectDropDown/DropDown";

import { convertToKg, valueGraterThanZero } from "../../utils/utils";
import "./costAndVolume.scss";
interface VolumeAndCostData {
  _id: string;
  volume?: number | null;
  volumeType?: string;
  cost?: number;
  ownerId?: string;
  sourceId?: string;
  year?: number;
}

interface VolumeAndCostProps {
  volumeAndCost: VolumeAndCostData[];
  addVolumeAndCostData: (data: VolumeAndCostData) => void;
  updateVolumeAndCostData: (data: VolumeAndCostData) => void;
  deleteVolumeAndCostData: (volumeAndCostId: string) => void;
  unitSize?: number;
  unitType?: string;
  testId?: string;
}

export const VolumeAndCost = (props: VolumeAndCostProps) => {
  const {
    volumeAndCost,
    addVolumeAndCostData,
    updateVolumeAndCostData,
    deleteVolumeAndCostData,
    unitSize = 1,
    unitType = "kg",
    testId = "volume-and-cost"
  } = props;

  const [volumeType, setVolumeType] = useState<any>("L");
  const [isEditId, setIsEditId] = useState<any>(null);
  const [visibleDeleteModal, setVisibleDeleteModal] = useState<any>(false);
  const [deleteId, setDeleteId] = useState<any>(null);
  const [year, setYear] = useState<any>(new Date());
  const currentYear = new Date().getFullYear();

  const values = useMemo(() => {
    setYear(currentYear);
    return {
      fromDate: null,
      toDate: null,
      volume: 0,
      volumeType: UNIT_TYPES[0].value,
      cost: 0,
      year: Number(currentYear)
    };
  }, [currentYear]);

  const {
    formState: { errors: errorsUnit },
    handleSubmit: handleSubmitVolume,
    reset,
    control,
    setValue
  } = useForm<VolumeAndCostData>({
    defaultValues: values
  });

  // const watchedValues = watch(["fromDate", "toDate"]);

  const resetState = () => {
    setVolumeType(UNIT_TYPES[0].value);
    setIsEditId(null);
    reset(values);
    setYear(currentYear);
  };

  const checkYearValidation = (data: any) => {
    const currentInsertToDate = data.year;
    const currentVolumeAndCost = !isEmpty(isEditId)
      ? volumeAndCost.filter((e: any) => e._id !== isEditId)
      : volumeAndCost;

    const hasSameYear = currentVolumeAndCost.some(item => {
      const itemYear = item?.year;
      return itemYear === currentInsertToDate;
    });

    if (hasSameYear) {
      warningMessage("This year has already recode in cost and volume");
      return false;
    }
    return true;
  };

  const generateYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [currentYear, currentYear - 1, currentYear - 2];

    return years.map(year => ({
      key: year,
      text: year.toString(),
      value: year
    }));
  };

  const updateValueAndCostData = (data: VolumeAndCostData) => {
    if (!checkYearValidation(data)) return;
    data.volumeType = volumeType;
    updateVolumeAndCostData(data);
    resetState();
  };

  const addValueAndCostData = (data: VolumeAndCostData) => {
    if (!checkYearValidation(data)) return;
    addVolumeAndCostData(data);
    resetState();
  };

  const onClickUpdate = (data: any) => {
    setVolumeType(data?.volumeType || "");
    setYear(data?.year);
    reset(data);
    setIsEditId(data?._id);
  };

  const loadTableData = () => {
    return volumeAndCost?.map((data: any, index: number) => {
      const numberOfUnits = data?.volume ?? 0;
      const unitSizeInKg = convertToKg(unitSize, unitType);
      const calculatedVolume = numberOfUnits * unitSizeInKg;
      return (
        <Table.Row className="tbleR" key={index} data-testid={`${testId}-row-${index}`}>
          <Table.Cell>
            <p>{data?.year ?? ""}</p>
          </Table.Cell>
          <Table.Cell>
            <p>{numberOfUnits || ""}</p>
          </Table.Cell>
          <Table.Cell>
            <p>{calculatedVolume}</p>
          </Table.Cell>
          <Table.Cell>
            <p>{data?.cost ?? ""}</p>
          </Table.Cell>
          <Table.Cell>
            <Grid>
              <Grid.Column computer={8} tablet={16} mobile={16}>
                {isEditId !== data?._id ? (
                  <MdModeEditOutline
                    cursor={"pointer"}
                    onClick={() => {
                      onClickUpdate(data);
                    }}
                    size={24}
                    color="var(--tableEditIcon)"
                    data-testid={`${testId}-row-${index}-edit-icon`}
                  />
                ) : (
                  <MdClear
                    cursor={"pointer"}
                    onClick={() => {
                      resetState();
                    }}
                    size={24}
                    color="var(--tableEditIcon)"
                    data-testid={`${testId}-row-${index}-clear-icon`}
                  />
                )}
              </Grid.Column>
              <Grid.Column computer={8} tablet={16} mobile={16}>
                <MdDeleteOutline
                  cursor={"pointer"}
                  size={24}
                  color="var(--tableEditIcon)"
                  onClick={() => {
                    setDeleteId(data?._id);
                    setVisibleDeleteModal(true);
                  }}
                  data-testid={`${testId}-row-${index}-delete-icon`}
                />
              </Grid.Column>
            </Grid>
          </Table.Cell>
        </Table.Row>
      );
    });
  };

  const updateButton = () => {
    return <p style={{ color: "#FFFF" }}>Update</p>;
  };

  const resetCostVolumeDeleteStatus = () => {
    setDeleteId(null);
    setVisibleDeleteModal(false);
  };

  const setCurrentDateYear = (currentYear: any) => {
    const isDataAvailable = volumeAndCost.find(item => {
      const itemYear = item?.year;
      return itemYear === currentYear;
    });

    if (isDataAvailable) {
      return onClickUpdate(isDataAvailable);
    }
    reset();
    setYear(currentYear);
    setValue("year", currentYear);
    setValue("volume", 0);
    setValue("cost", 0);
    setVolumeType("");
    setIsEditId(null);
  };

  // Todo enable this in future
  // const setFromData = (e: any) => {
  //   setDateFrom(new Date(e));
  //   setDateTo("");
  //   setValue("fromDate", new Date(e).toISOString());
  // };

  // const setToData = (e: any) => {
  //   setDateTo(new Date(e));
  //   setValue("toDate", new Date(e).toISOString());
  // };

  const addOrUpdateCostVolume = (data: any) => {
    if (isEditId) {
      updateValueAndCostData(data);
      return;
    }
    addValueAndCostData(data);
  };

  // Todo enable this in future
  // const toMaxDate = dateFrom ? new Date(dateFrom.getFullYear(), 11, 31) : null;

  // const disableAddUpdateButton = useMemo(() => {
  //   return !dateFrom || !dateTo;
  // }, [dateFrom, dateTo]);

  return (
    <div>
      <Grid>
        <Grid.Column computer={16} tablet={16} mobile={16} className="userBankDetailsMain">
          <div>
            <Grid>
              <Grid.Column computer={4} tablet={8} mobile={16}>
                <DropDown
                  required={true}
                  labelName={"Year"}
                  placeholder="Year"
                  currentData={generateYearOptions()}
                  defaultValue={year}
                  customGridColumn={"customGridColomnType"}
                  handleChangeState={(e: any, { value }: any) => {
                    setCurrentDateYear(value);
                  }}
                  testId={`${testId}-year-dropdown`}
                />
              </Grid.Column>
              {/* Todo enable this in future
               <Grid.Column computer={4} tablet={8} mobile={16}>
                <SelectDatePicker
                  currentDate={dateFrom}
                  selectDate={(e: any) => setFromData(e)}
                  title="From"
                />
              </Grid.Column>
              <Grid.Column computer={4} tablet={8} mobile={16}>
                <SelectDatePicker
                  currentDate={dateTo}
                  minDate={dateFrom}
                  maxDate={toMaxDate}
                  selectDate={(e: any) => setToData(e)}
                  title="To"
                />
              </Grid.Column> */}
              <Grid.Column computer={3} tablet={8} mobile={16}>
                <InputNumber
                  labelName="Number of units"
                  control={control}
                  errors={errorsUnit.volume}
                  placeholder="Number of units"
                  name="volume"
                  otherValidation={{
                    validate: {
                      valueCheck: (v: any) =>
                        valueGraterThanZero(v) || "Number of units must be greater than 0"
                    }
                  }}
                  testId={`${testId}-volume-input`}
                />
              </Grid.Column>
              <Grid.Column computer={3} tablet={8} mobile={16}>
                <InputNumber
                  control={control}
                  errors={errorsUnit.cost}
                  labelName={"Price per unit"}
                  placeholder="Price per unit"
                  name="cost"
                  otherValidation={{
                    validate: {
                      valueCheck: (v: any) =>
                        valueGraterThanZero(v) || "Price must be grater than 0 "
                    }
                  }}
                  testId={`${testId}-cost-input`}
                />
              </Grid.Column>
              <Grid.Column computer={2} tablet={8} mobile={16}>
                <CustomButton
                  theme="green"
                  icon
                  iconChild={!isEditId ? <PlusIcon /> : updateButton()}
                  customColumnStyle={"addCostVolume"}
                  customBase="productUnitAddButton"
                  onClick={handleSubmitVolume(data => addOrUpdateCostVolume(data))}
                  testId={`${testId}-${isEditId ? "update" : "add"}-button`}
                />
              </Grid.Column>
            </Grid>
            <Grid.Column computer={16} tablet={16} mobile={16} className="productRegisterTable">
              <CommonTable tableHeaderData={ValueAndCostTable} testId={`${testId}-table`}>
                {loadTableData()}
              </CommonTable>
            </Grid.Column>
          </div>
        </Grid.Column>
      </Grid>
      <ConfirmModal
        viewModal={visibleDeleteModal}
        closeModal={() => resetCostVolumeDeleteStatus()}
        cancel={() => resetCostVolumeDeleteStatus()}
        approve={() => {
          deleteVolumeAndCostData(deleteId);
          resetCostVolumeDeleteStatus();
        }}
        title="Delete Cost and Volume Data"
        subTitle="Are you sure you want to delete cost and volume data?"
        testId={`${testId}-delete-modal`}
      />
    </div>
  );
};
